<script setup lang="ts">
  import { type HTMLAttributes, computed } from 'vue'
  import {
    PopoverContent,
    type PopoverContentEmits,
    type PopoverContentProps,
    PopoverPortal,
    useForwardPropsEmits
  } from 'radix-vue'
  import { cn } from '@/lib/utils'

  defineOptions({
    inheritAttrs: false
  })

  const props = withDefaults(
    defineProps<
      PopoverContentProps & {
        class?: HTMLAttributes['class']
        variant?: 'default' | 'document-list'
      }
    >(),
    {
      align: 'center',
      sideOffset: 4,
      variant: 'default'
    }
  )
  const emits = defineEmits<PopoverContentEmits>()

  const delegatedProps = computed(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { class: _, variant: __, ...delegated } = props

    return delegated
  })

  const forwarded = useForwardPropsEmits(delegatedProps, emits)
</script>

<template>
  <PopoverPortal>
    <PopoverContent
      v-bind="{ ...forwarded, ...$attrs }"
      :class="
        cn(
          `z-50 rounded-md border bg-popover text-popover-foreground shadow-md outline-none
          data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0
          data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95
          data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2
          data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2`,
          {
            'w-72 p-4': props.variant === 'default',
            'w-[320px] p-0': props.variant === 'document-list'
          },
          props.class
        )
      "
    >
      <slot />
    </PopoverContent>
  </PopoverPortal>
</template>
